import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { fmtSize } from "@/lib/files";
import { DocIcon } from "@/components/leads/LeadDocumentsField";
import { Building2, Clock, Download, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 font-medium">{value || "—"}</div>
    </div>
  );
}

/* Public, login-free page behind an expiring share token: lead summary + downloadable property documents. */
export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/share/${token}`, { timeout: 20000 })
      .then((r) => { if (alive) setData(r.data); })
      .catch((err) => { if (alive) setError(err?.response?.status === 410 ? "This link has expired. Ask the sender to share the documents again." : err?.response?.data?.detail || "This link is invalid or has been revoked."); });
    return () => { alive = false; };
  }, [token]);

  const lead = data?.lead || {};
  const location = [lead.city, lead.location, lead.pincode].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="share-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-slate-950 text-white flex items-center justify-center"><Building2 className="h-5 w-5" /></div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-slate-950">Rana Associates</div>
              <div className="text-xs text-slate-500">Property documents · secure share</div>
            </div>
          </Link>
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><ShieldCheck className="h-4 w-4" /> Encrypted link</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {error ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-900 flex items-start gap-3" data-testid="share-error">
            <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <div><div className="font-semibold">Link unavailable</div><div className="text-sm mt-1">{error}</div></div>
          </div>
        ) : !data ? (
          <div className="flex items-center gap-2 text-slate-500" data-testid="share-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading documents…</div>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">Valuation lead</p>
            <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-bold text-slate-950" data-testid="share-customer">{lead.customer_name}</h1>
            <p className="text-slate-600 mt-1" data-testid="share-address">{lead.property_address}</p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 rounded-lg border border-slate-200 bg-white p-4">
              <Row label="Location" value={location} />
              <Row label="Bank / NBFC" value={lead.bank_name ? `${lead.bank_name}${lead.branch ? ` · ${lead.branch}` : ""}` : null} />
              <Row label="Valuation" value={(lead.valuation_type || "").toUpperCase()} />
              <Row label="Paper ref" value={lead.property_paper_ref} />
            </div>
            <div className="mt-6 rounded-lg border border-slate-200 bg-white">
              <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold uppercase tracking-widest text-slate-500">Documents · {data.documents.length}</div>
              <ul className="divide-y divide-slate-100" data-testid="share-doc-list">
                {data.documents.map((d) => (
                  <li key={d.file_id} className="flex items-center gap-3 px-4 py-3" data-testid={`share-doc-${d.file_id}`}>
                    <DocIcon kind={d.kind} className="h-5 w-5" />
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{d.name}</div><div className="text-[11px] text-slate-400">{fmtSize(d.size)}</div></div>
                    <a href={`${API}/share/${token}/${d.file_id}`} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 rounded-md bg-slate-950 text-white text-xs font-semibold px-3 py-2 hover:bg-slate-800" data-testid={`share-doc-download-${d.file_id}`}>
                      <Download className="h-3.5 w-3.5" /> Open
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Link expires {fmtDate(data.expires_at)}</span>
              {data.shared_by && <span>Shared by {data.shared_by} ({data.shared_by_role})</span>}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
